/**
 * VoicemailPlayer — small play/pause button + duration for a single
 * voicemail row. Lazy-loads the audio bytes on first play and caches
 * them in memory so re-plays skip the network round-trip.
 *
 * Mark-as-read fires the first time play() resolves (i.e. the audio
 * actually started, not just on tap). We intentionally don't mark as
 * read on pause/cancel — a user who taps by accident shouldn't lose
 * the "unread" badge.
 */
import { useEffect, useRef, useState } from 'react';
import { voicemailsApi } from '@/lib/api';
import { base64ToBlob } from '@/lib/ringChat';
import { PlaySprite, PauseSprite, LoadingSprite } from '@/components/Sprites';

interface VoicemailPlayerProps {
  voicemailId: string;
  durationMs: number | null;
  onPlayed?: () => void;
}

function formatDuration(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return '–:––';
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VoicemailPlayer({ voicemailId, durationMs, onPlayed }: VoicemailPlayerProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  // Cached blob URL so re-plays skip the network. We rebuild on voicemailId
  // change so the player is reusable across rows (key it in the parent).
  const audioUrlRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const markedRef = useRef(false);

  // Tear down audio URL on unmount or when the voicemailId changes —
  // releasing the blob URL is essential to avoid leaking memory across
  // many voicemails (each base64 blob decodes to ~100KB binary).
  useEffect(() => {
    markedRef.current = false;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, [voicemailId]);

  const play = async () => {
    if (state === 'loading') return;
    try {
      if (!audioUrlRef.current) {
        setState('loading');
        const res = await voicemailsApi.fetchAudio(voicemailId);
        const blob = base64ToBlob(res.audio_base64, res.audio_mime);
        audioUrlRef.current = URL.createObjectURL(blob);
      }
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrlRef.current);
        audioRef.current.preload = 'auto';
        audioRef.current.onended = () => setState('idle');
        audioRef.current.onerror = () => {
          setState('error');
          // Invalidate the cached URL — a bad blob shouldn't poison
          // future plays. Re-fetching next time will get a fresh copy.
          if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
        };
      }
      await audioRef.current.play();
      setState('playing');
      // Mark as read the first time play() succeeds for this row. Idempotent
      // on the server (COALESCE in the SQL), but we still guard client-side
      // to avoid spamming the network on pause/play cycles.
      if (!markedRef.current) {
        markedRef.current = true;
        voicemailsApi.markRead(voicemailId).then(() => onPlayed?.()).catch(() => { /* swallow */ });
      }
    } catch (err) {
      console.error('voicemail play failed', err);
      setState('error');
    }
  };

  const pause = () => {
    audioRef.current?.pause();
    setState('idle');
  };

  const label = state === 'playing' ? 'Pausar' : state === 'loading' ? 'Cargando' : 'Reproducir';

  return (
    <button
      type="button"
      onClick={state === 'playing' ? pause : play}
      aria-label={label}
      data-testid={`voicemail-play-${voicemailId}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 6px',
        background: 'var(--gb-mid)',
        color: 'var(--gb-bg)',
        border: '1px solid var(--gb-dark)',
        borderRadius: 2,
        fontSize: 7,
        lineHeight: 1.1,
        cursor: 'pointer',
        fontFamily: 'var(--pixel)',
        minHeight: 18,
        flexShrink: 0,
      }}
    >
      {state === 'loading' ? (
        <LoadingSprite size={10} />
      ) : state === 'playing' ? (
        <PauseSprite size={10} />
      ) : (
        <PlaySprite size={10} />
      )}
      <span style={{ fontWeight: 'bold' }}>
        {state === 'loading' ? '…' : formatDuration(durationMs)}
      </span>
    </button>
  );
}